import type { LogEntry } from "../types";

function toMillis(t: Date | string | number | unknown): number {
  if (t instanceof Date) return t.getTime();
  if (typeof t === "number") return t;
  return new Date(t as string).getTime();
}

/**
 * Combine several entries that share a day + period into one record for
 * display, so focused single-task entries logged close together "line up" as
 * one event on the report grid (a 7:00 glucose check and a 7:03 meal read as
 * one breakfast cell). Display-only - never persisted, and the underlying
 * entries remain individually editable/deletable. See docs/add-task-flows.md.
 *
 * Glucose and ketone take the earliest defined value (e.g. the pre-meal
 * reading); insulin and carbs are summed across the group.
 */
export function mergeEntriesForDisplay(entries: LogEntry[]): LogEntry {
  if (entries.length <= 1) return entries[0];

  const sorted = [...entries].sort(
    (a, b) => toMillis(a.timestamp) - toMillis(b.timestamp),
  );

  const sum = (
    pick: (e: LogEntry) => number | undefined,
  ): number | undefined => {
    let total: number | undefined;
    for (const e of sorted) {
      const v = pick(e);
      if (typeof v === "number") total = (total ?? 0) + v;
    }
    return total;
  };

  const first = <T>(pick: (e: LogEntry) => T | undefined): T | undefined => {
    for (const e of sorted) {
      const v = pick(e);
      if (v !== undefined && v !== null) return v;
    }
    return undefined;
  };

  const notes = sorted
    .map((e) => e.notes)
    .filter((n): n is string => !!n && n.trim() !== "");

  return {
    ...sorted[0],
    timestamp: sorted[0].timestamp,
    bloodGlucose: first((e) => e.bloodGlucose),
    ketones: first((e) => e.ketones),
    carbs: sum((e) => e.carbs),
    qaBase: sum((e) => e.qaBase),
    qaCorrection: sum((e) => e.qaCorrection),
    pfBase: sum((e) => e.pfBase),
    biBase: sum((e) => e.biBase),
    biCorrection: sum((e) => e.biCorrection),
    exercise: first((e) => e.exercise),
    sickDay: first((e) => e.sickDay),
    notes: notes.length ? notes.join("; ") : undefined,
  };
}
